import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  notification,
  Radio,
  Row,
  Select,
  Switch,
  Table,
  Typography,
  Upload,
} from "antd";
import {
  AFTER_DISBURSE_IOU_LIST,
  BGMEA_UD_AMENDMENT_ADD_ENDPOINT,
  BGMEA_UD_AMENDMENT_EDIT_ENDPOINT,
  EXIM_ACCOUNT_LIST_ENDPOINT,
  IOU_COST_LIST_ENDPOINT,
  SALES_CONTRACT_COMMERCIAL_LIST,
  UD_NUMBERS_LIST,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

// Constants
const DEFAULT_HEADER_ID = 4;
const DEFAULT_CURRENCY = "BDT";

/**
 * Main Component
 */
const BgmeaUdAmendmentForm = forwardRef((props, ref) => {
  const { form, isEdit, editInfo, viewMode } = props;
  const costWatcher = Form.useWatch("cost", form);
  const usedQuantityWatcher = Form.useWatch("used_quantity", form);
  const isActiveFocCertificateChargedWatcher = Form.useWatch("is_active_foc_certificate_charged", form);
  const [haveFOCCertificateCharged, setHaveFOCCertificateCharged] = useState(null);
  const navigate = useNavigate();

  // State management
  const [options, setOptions] = useState({
    amendments: [],
    accounts: [],
    udNumbers: [],
    iou: [],
  });
  const [fileList, setFileList] = useState([]);
  const [miscellaneousCost, setMiscellaneousCost] = useState("");
  const [commentMiscellaneousCost, setCommentMiscellaneousCost] = useState("");

  // handle file upload change
  const handleFileChange = useCallback(({ fileList: newFileList }) => {
    setFileList(newFileList);
  }, []);

  const resetFiles = useCallback(() => {
    setFileList([]);
  }, []);

  const addFile = useCallback((file) => {
    setFileList((prev) => [...prev, file]);
  }, []);

  // Handle miscellaneous cost change
  const handleMiscellaneousCostChange = useCallback((e) => {
    const value = e.target.value;
    setMiscellaneousCost(value);
  }, []);

  // Handle comment miscellaneous cost change
  const handleCommentMiscellaneousCostChange = useCallback((e) => {
    const value = e.target.value;
    setCommentMiscellaneousCost(value);
  }, []);

  // Get Api service
  const getSalesContractNumber = async (filter) => {
    const body = {
      page: 1,
      per_page: 10,
      ...(filter?.reference_no && { reference_no: filter.reference_no }),
    };
    const response = await getData(`${SALES_CONTRACT_COMMERCIAL_LIST}`, null, body);
    if (response?.status === 200) {
      setOptions((prev) => ({
        ...prev,
        salesContracts: response?.data?.data?.data || [],
      }));
    }
  };

  const getEximAccount = async () => {
    const response = await getData(EXIM_ACCOUNT_LIST_ENDPOINT, null, {
      per_page: 100000,
    });
    if (response?.status === 200) {
      const accounts = response?.data?.data?.data || [];
      setOptions((prev) => ({
        ...prev,
        accounts: accounts,
      }));
    }
  };

  const getUdNumbers = async (filter) => {
    const body = {
      page: 1,
      per_page: 100000, // Load all UD numbers for client-side filtering
      ...(filter?.ud_number && { ud_number: filter.ud_number }),
    };
    const response = await getData(UD_NUMBERS_LIST, null, body);
    if (response?.status === 200) {
      setOptions((prev) => ({
        ...prev,
        udNumbers: response?.data?.data || [],
      }));
    }
  };

  const getCharges = useCallback(async () => {
    const response = await getData(`/api/exim-cost/getTypebyHead/?cost_head_id=${DEFAULT_HEADER_ID}`);
    if (response?.status === 200) {
      const chargeGroups = new Map();
      const regex = /(.+?)#(Urgent|Regular)$/i;

      // Group charges by base name
      response.data.forEach((charge) => {
        const match = charge.type.name.match(regex);

        if (charge.type.name.toLowerCase().includes("foc certificate")) {
          setHaveFOCCertificateCharged({
            key: charge.id,
            type: charge.type.name,
            exim_charge_id: charge.id,
            exim_charge_amount: charge.amount,
            cost_type_id: charge.cost_type_id,
            charges: [],
            regular_amount: null,
            urgent_amount: null,
            issue_charge_type: "urgent", // default selection
          });
          return; // Skip adding to chargeGroups
        }

        if (match) {
          const baseName = match[1].trim();
          const chargeType = match[2].toLowerCase();

          if (!chargeGroups.has(baseName)) {
            chargeGroups.set(baseName, {
              key: charge.id,
              type: baseName,
              exim_charge_id: null, // Will be set based on urgent preference
              exim_charge_amount: null, // Will be set based on urgent preference
              cost_type_id: charge.cost_type_id,
              charges: [],
              regular_amount: null,
              urgent_amount: null,
              issue_charge_type: "urgent", // default selection
            });
          }

          const group = chargeGroups.get(baseName);
          group.charges.push({
            label: baseName,
            value: charge.id,
            amount: charge.amount,
            type: chargeType,
          });

          // Set amounts based on charge type
          if (chargeType === "regular") {
            group.regular_amount = charge.amount;
            // Only set as default if urgent doesn't exist
            if (!group.urgent_amount && !group.exim_charge_id) {
              group.exim_charge_amount = charge.amount;
              group.exim_charge_id = charge.id;
            }
          } else if (chargeType === "urgent") {
            group.urgent_amount = charge.amount;
            // Always prefer urgent as default
            group.exim_charge_amount = charge.amount;
            group.exim_charge_id = charge.id;
          }
        } else {
          // Handle charges without #Urgent or #Regular
          const baseName = charge.type.name;
          if (!chargeGroups.has(baseName)) {
            chargeGroups.set(baseName, {
              key: charge.id,
              type: baseName,
              exim_charge_id: charge.id,
              exim_charge_amount: charge.amount,
              cost_type_id: charge.cost_type_id,
              charges: [
                {
                  label: baseName,
                  value: charge.id,
                  amount: charge.amount,
                },
              ],
              regular_amount: charge.amount,
              urgent_amount: charge.amount,
              issue_charge_type: "urgent",
            });
          }
        }
      });

      // Convert Map to Array
      const costTypes = Array.from(chargeGroups.values());

      // Set initial form values for costs
      form.setFieldsValue({ cost: costTypes });
    }
  }, [form]);

  // Handle radio button change to update amount
  const handleIssueChargeTypeChange = useCallback(
    (value, fieldName) => {
      const currentCosts = form.getFieldValue("cost") || [];
      const updatedCosts = [...currentCosts];
      const costIndex = parseInt(fieldName);

      if (updatedCosts[costIndex]) {
        const currentCost = updatedCosts[costIndex];

        // Find the appropriate charge from the charges array
        const selectedCharge = currentCost.charges?.find((charge) => charge.type === value);

        if (selectedCharge) {
          updatedCosts[costIndex] = {
            ...currentCost,
            issue_charge_type: value,
            exim_charge_amount: selectedCharge.amount,
            exim_charge_id: selectedCharge.value,
          };
        } else {
          // Fallback to stored amounts if charges array doesn't have the type
          const newAmount = value === "urgent" ? currentCost.urgent_amount : currentCost.regular_amount;

          updatedCosts[costIndex] = {
            ...currentCost,
            issue_charge_type: value,
            exim_charge_amount: newAmount,
          };
        }

        form.setFieldsValue({ cost: updatedCosts });
      }
    },
    [form],
  );

  const fetchInitialData = useCallback(async () => {
    await getEximAccount();
    await getIOU();
    if (!isEdit) {
      await getSalesContractNumber();
      await getUdNumbers();
      await getCharges();
      resetFiles();
      // Set initial used_quantity to 0 in add mode
      form.setFieldsValue({ used_quantity: 0 });
    }
  }, [isEdit, resetFiles, getCharges, form]);

  // Initial get data on component mount
  useEffect(() => {
    fetchInitialData(isEdit);
  }, [isEdit, fetchInitialData]);

  // Effect to handle service charge visibility based on used quantity
  useEffect(() => {
    const costs = form.getFieldValue("cost") || [];
    const serviceChargeIndex = costs.findIndex((cost) => cost?.type?.toLowerCase().includes("service charge"));

    if (serviceChargeIndex !== -1) {
      // Clone the costs array to modify it
      const updatedCosts = [...costs];

      if (usedQuantityWatcher > 0) {
        // If used quantity > 0, calculate the service charge
        const newAmount = ((usedQuantityWatcher || 0) / 12) * updatedCosts[serviceChargeIndex]?.urgent_amount;

        updatedCosts[serviceChargeIndex] = {
          ...updatedCosts[serviceChargeIndex],
          exim_charge_amount: parseFloat(newAmount.toFixed(2)),
          _visible: true, // Flag to track visibility
        };
      } else {
        // If used quantity is 0 or not set, set service charge to 0
        updatedCosts[serviceChargeIndex] = {
          ...updatedCosts[serviceChargeIndex],
          exim_charge_amount: 0,
          _visible: false, // Flag to track visibility
        };
      }

      form.setFieldsValue({ cost: updatedCosts });
    }
  }, [usedQuantityWatcher, form]);

  useEffect(() => {
    if (isActiveFocCertificateChargedWatcher !== null) {
      if (isActiveFocCertificateChargedWatcher) {
        // If checked, add FOC Certificate charge if not already present
        if (
          haveFOCCertificateCharged &&
          !(form.getFieldValue("cost") || []).some(
            (cost) => cost.exim_charge_id === haveFOCCertificateCharged.exim_charge_id,
          )
        ) {
          const currentCosts = form.getFieldValue("cost") || [];
          form.setFieldsValue({
            cost: [...currentCosts, haveFOCCertificateCharged],
          });
        }
      } else {
        // If unchecked, remove FOC Certificate charge if present
        const currentCosts = form.getFieldValue("cost") || [];
        const updatedCosts = currentCosts.filter(
          (cost) => cost.exim_charge_id !== haveFOCCertificateCharged?.exim_charge_id,
        );
        form.setFieldsValue({ cost: updatedCosts });
      }
    }
  }, [isActiveFocCertificateChargedWatcher, haveFOCCertificateCharged, form]);

  // Separate effect to handle editInfo changes
  useEffect(() => {
    if (isEdit && editInfo) {
      // First load charges to get the structure with radio button data
      getCharges().then(() => {
        // Map existing costs to form fields with radio button support
        const existingCosts = editInfo.lien_charges || [];
        const currentCosts = form.getFieldValue("cost") || [];

        const costFields = existingCosts.map((charge, index) => {
          // Find matching charge in current costs to get the charges array
          const matchingCost = currentCosts.find((cost) => cost.type === charge.charges.type.name.split("#")[0].trim());

          // Extract charge type from name if it contains #Urgent or #Regular
          const regex = /(.+?)#(Urgent|Regular)$/i;
          const match = charge.charges.type.name.match(regex);
          const issueChargeType = match ? match[2].toLowerCase() : "urgent";

          if (charge.charges.type.name.toLowerCase().includes("foc certificate")) {
            setHaveFOCCertificateCharged({
              key: charge.charges.id,
              type: charge.charges.type.name,
              exim_charge_id: charge.charges.id,
              exim_charge_amount: charge.exim_charge_amount,
              cost_type_id: charge.charges.cost_type_id,
              charges: [],
              regular_amount: null,
              urgent_amount: null,
              issue_charge_type: "urgent", // default selection
            });
            form.setFieldsValue({
              is_active_foc_certificate_charged: true,
            });
          }

          return {
            key: charge.charges.id,
            type: match ? match[1].trim() : charge.charges.type.name,
            exim_charge_id: charge.charges.id,
            exim_charge_amount: charge.exim_charge_amount,
            cost_type_id: charge.charges.cost_type_id,
            issue_charge_type: charge.issue_charge_type || issueChargeType,
            charges: matchingCost?.charges || [],
            regular_amount: matchingCost?.regular_amount || charge.exim_charge_amount,
            urgent_amount: matchingCost?.urgent_amount || charge.exim_charge_amount,
          };
        });

        getSalesContractNumber({
          reference_no: editInfo?.sales_contract?.sale_contract_info?.reference_no,
        });

        setOptions((prev) => ({
          ...prev,
          amendments: editInfo?.ud_details?.sale_contract_amendment_info
            ? editInfo.ud_details.sale_contract_amendment_info
                ?.filter((value) => value?.name !== "Initial")
                .map((amendment) => ({
                  label: amendment.name,
                  value: amendment.id,
                }))
            : [],
        }));

        getUdNumbers();

        form.setFieldsValue({
          actual_sales_contract_id: editInfo?.ud_details?.sales_contract_id,
          sales_contract_id: editInfo?.ud_details?.sales_contract_id,
          sales_contract_reference_no: editInfo?.ud_details?.sale_contract_info?.reference_no || "",
          ud_number: Number(editInfo?.ud_number) || null,
          salesContractValue: editInfo?.ud_details?.sales_contract_value,
          quantity: editInfo?.ud_details?.order_quantity || 0,
          date: editInfo?.ud_details?.sale_contract_info?.contract_date
            ? moment(editInfo?.ud_details?.sale_contract_info?.contract_date)
            : null,
          cost: costFields,
          used_quantity: editInfo?.used_quantity || 0,
          amendment_id: Number(editInfo?.amendment_id) || "",
          amendment_date: editInfo?.amendment_date ? moment(editInfo?.amendment_date) : null,
          trn_date: editInfo?.trn_date ? moment(editInfo?.trn_date) : null,
          ud_date: editInfo?.ud_date ? moment(editInfo?.ud_date) : null,
          miscellenous_cost: editInfo?.miscellenous_cost || "",
          comment_miscellenous_cost: editInfo?.comment_miscellenous_cost || "",
          iou_id: editInfo?.iou_info?.id,
        });

        if (editInfo?.iou_info) {
          const iouExists = options.iou.filter((iou) => iou.id === editInfo.iou_info.id).length > 0;

          if (!iouExists) {
            setOptions((prev) => ({
              ...prev,
              iou: [
                {
                  id: editInfo.iou_info.id,
                  iou_number: editInfo.iou_info.iou_number,
                },
                ...prev.iou,
              ],
            }));
          }
        }

        // Initialize miscellaneous cost state from editInfo
        setMiscellaneousCost(editInfo?.miscellenous_cost || "");
        setCommentMiscellaneousCost(editInfo?.comment_miscellenous_cost || "");
      });

      // Handle voucher file
      if (editInfo?.voucher) {
        // Clear existing files first to avoid duplication
        setFileList([]);
        addFile({
          uid: "-1",
          name: editInfo?.voucher?.split("/").pop(),
          status: "done",
          url: process.env.REACT_APP_BASE_URL + "/" + editInfo.voucher,
        });
      }
    }
  }, [editInfo, isEdit, form, addFile, getCharges]);

  // on search UD numbers - kept for potential future use
  // Implemented client-side filtering instead

  // on select UD number
  const onSelectUdNumber = useCallback(
    async (value) => {
      try {
        const selectedUdNumber = options.udNumbers.find((udNumber) => udNumber.id === value);

        if (selectedUdNumber) {
          // Prepare all form values to set at once
          const formValues = {
            salesContractValue: selectedUdNumber?.sales_contract_value || 0,
            quantity: selectedUdNumber?.order_quantity || 0,
          };

          // Set Sales Contract/Master LC field with reference number
          if (selectedUdNumber?.sale_contract_info?.reference_no) {
            formValues.sales_contract_reference_no = selectedUdNumber.sale_contract_info.reference_no;
            formValues.sales_contract_id = selectedUdNumber.sales_contract_id;
            formValues.actual_sales_contract_id = selectedUdNumber.sales_contract_id;
          }

          // Set contract date
          if (selectedUdNumber?.sale_contract_info?.contract_date) {
            const contractDate = selectedUdNumber.sale_contract_info.contract_date;
            if (contractDate) {
              formValues.date = moment(contractDate);
            }
          }

          // Set UD date if available (showing UD date after selecting UD Number)
          if (selectedUdNumber?.ud_date) {
            formValues.ud_date = moment(selectedUdNumber.ud_date);
          }

          // Set all form values at once
          form.setFieldsValue(formValues);

          // Set Amendment dropdown based on sale_contract_amendment_info
          if (
            selectedUdNumber?.sale_contract_amendment_info &&
            selectedUdNumber.sale_contract_amendment_info.length > 0
          ) {
            // Get all available amendments for the dropdown
            const amendments = selectedUdNumber.sale_contract_amendment_info;
            setOptions((prev) => ({
              ...prev,
              amendments: amendments
                .filter((value) => value?.name !== "Initial")
                .map((amendment) => ({
                  label: amendment.name,
                  value: amendment.id,
                })),
            }));
          } else {
            // Clear amendments if none available
            setOptions((prev) => ({ ...prev, amendments: [] }));
            form.setFieldsValue({ amendment_id: "" });
          }
        }
      } catch (error) {}
    },
    [options.udNumbers, form],
  );

  const total =
    (costWatcher?.reduce((sum, cost) => {
      const amount = parseFloat(cost.exim_charge_amount) || 0;
      return sum + amount;
    }, 0) || 0) + (parseFloat(miscellaneousCost) || 0);

  // Form submission
  const handleSubmit = useCallback(
    async (values) => {
      try {
        const costs =
          values.cost?.map((cost) => ({
            exim_charge_id: cost.exim_charge_id,
            exim_charge_amount: cost.exim_charge_amount,
          })) || [];

        const formData = new FormData();
        formData.append("ud_number", values.ud_number);
        formData.append("ud_date", values.ud_date);
        formData.append("amendment_id", values.amendment_id);
        formData.append("cost", JSON.stringify(costs));
        formData.append("used_quantity", values.used_quantity || 0);
        formData.append("miscellenous_cost", miscellaneousCost || "");
        formData.append("comment_miscellenous_cost", commentMiscellaneousCost || "");
        // formData.append("iou_id", values.iou_id);
        if (values?.iou_id) {
          formData.append("iou_id", values.iou_id);
        }

        // Add Amendment Date in YYYY-MM-DD format
        if (values.amendment_date && moment.isMoment(values.amendment_date)) {
          formData.append("amendment_date", values.amendment_date.format("YYYY-MM-DD"));
        }

        // Add TRN Date in YYYY-MM-DD format
        if (values.trn_date && moment.isMoment(values.trn_date)) {
          formData.append("trn_date", values.trn_date.format("YYYY-MM-DD"));
        }

        // Add UD Date as string
        if (values.ud_date && moment.isMoment(values.ud_date)) {
          formData.append("ud_date", values.ud_date.format("YYYY-MM-DD"));
        }

        if (total) {
          formData.append("total", total);
        }

        if (fileList.length > 0 && fileList[0].originFileObj) {
          formData.append("voucher", fileList[0].originFileObj);
        }

        const query = isEdit ? `${BGMEA_UD_AMENDMENT_EDIT_ENDPOINT}/${editInfo?.id}` : BGMEA_UD_AMENDMENT_ADD_ENDPOINT;

        const response = await postData(query, formData);

        if (response && (response?.code === 200 || response?.status === 200)) {
          notification.success({
            message: "Success",
            description: response?.message || `BGMEA UD Amendment ${isEdit ? "updated" : "created"} successfully`,
            placement: "topRight",
          });
          navigate("/import-export-expense-tracking/bgmea-ud-amendment");
        }
      } catch (error) {}
    },
    [fileList, isEdit, editInfo, navigate, total, miscellaneousCost, commentMiscellaneousCost],
  );

  // Expose form instance
  useImperativeHandle(ref, () => ({ form }), [form]);

  // Table columns configuration
  const tableColumns = useMemo(
    () => [
      {
        title: "Type",
        dataIndex: "fieldName",
        key: "id",
        width: "20%",
        render: (fieldName) => {
          // Get the current cost data to check if charges array has variants
          const currentCosts = form.getFieldValue("cost") || [];
          const currentCost = currentCosts[fieldName];
          const hasChargeVariants =
            currentCost?.charges?.length > 1 ||
            currentCost?.charges?.some((charge) => charge.type === "urgent" || charge.type === "regular");

          return (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Form.Item
                name={[fieldName, "type"]}
                style={{
                  width: "100%",
                  marginBottom: hasChargeVariants ? 8 : 0,
                }}
                rules={[{ required: true, message: "Please select a Description" }]}
              >
                <Input disabled={true} />
              </Form.Item>
              {hasChargeVariants && (
                <Form.Item
                  name={[fieldName, "issue_charge_type"]}
                  style={{ width: "100%", marginBottom: 0 }}
                  initialValue="urgent"
                >
                  <Radio.Group
                    disabled={viewMode}
                    size="small"
                    onChange={(e) => handleIssueChargeTypeChange(e.target.value, fieldName)}
                  >
                    <Radio.Button value="urgent" style={{ fontSize: "11px", padding: "0 8px" }}>
                      Urgent
                    </Radio.Button>
                    <Radio.Button value="regular" style={{ fontSize: "11px", padding: "0 8px" }}>
                      Regular
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              )}
            </div>
          );
        },
      },
      {
        title: "Amount",
        dataIndex: "fieldName",
        key: "exim_charge_amount",
        width: "25%",
        render: (fieldName) => (
          <Form.Item name={[fieldName, "exim_charge_amount"]} noStyle>
            <InputNumber placeholder="Enter amount" style={{ width: "100%" }} disabled={true} min={0} />
          </Form.Item>
        ),
      },
    ],
    [viewMode, handleIssueChargeTypeChange, form],
  );

  const getIOU = async (filter) => {
    const body = {
      page: 1,
      per_page: 100,
      ...(filter?.iou_number && { iou_number: filter.iou_number }),
    };
    const response = await getData(`${AFTER_DISBURSE_IOU_LIST}`, null, body);

    if (response?.status === 200) {
      setOptions((prev) => ({
        ...prev,
        iou: response?.data?.data || [],
      }));
    }
  };

  return (
    <div className="containt-body">
      <Card>
        <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={true}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label="UD Number"
                name="ud_number"
                rules={[
                  {
                    required: true,
                    message: "Please select a UD Number",
                  },
                ]}
              >
                <Select
                  placeholder="UD Number"
                  onSelect={onSelectUdNumber}
                  disabled={viewMode}
                  showSearch
                  filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  // onSearch={onUdNumberSearch} // Using client-side filtering instead
                  options={options.udNumbers.map((udNumber) => ({
                    label: udNumber.ud_number,
                    value: udNumber.id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              {/* Hidden field to store the actual sales_contract_id in edit mode */}
              {isEdit && (
                <Form.Item name="actual_sales_contract_id" hidden={true}>
                  <Input type="hidden" />
                </Form.Item>
              )}

              {/* Hidden field to store sales_contract_id */}
              <Form.Item name="sales_contract_id" hidden={true}>
                <Input type="hidden" />
              </Form.Item>

              <Form.Item label="Sales Contract/Master LC" name="sales_contract_reference_no">
                <Input placeholder="Sales Contract/Master LC" disabled={true} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item label="Date" name="date">
                <DatePicker style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item label="Value" name="salesContractValue">
                <Input disabled={true} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item label="Quantity" name="quantity">
                <InputNumber placeholder="Quantity" style={{ width: "100%" }} disabled={true} min={0} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item label="UD Date" name="ud_date">
                <DatePicker style={{ width: "100%" }} disabled={viewMode} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item label="Used Quantity" name="used_quantity">
                <InputNumber
                  placeholder="Used Quantity"
                  style={{ width: "100%" }}
                  disabled={viewMode}
                  min={0}
                  // No need for onChange handler here since we're using the useEffect with usedQuantityWatcher
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                label="Select Amendment *"
                name="amendment_id"
                rules={[
                  {
                    required: true,
                    message: "Please select amendment",
                  },
                ]}
              >
                <Select
                  placeholder="Select Contract Amendment"
                  disabled={viewMode || options.amendments.length === 0}
                  options={options.amendments}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                label="Amendment Date *"
                name="amendment_date"
                rules={[
                  {
                    required: true,
                    message: "Please select amendment date",
                  },
                ]}
              >
                <DatePicker style={{ width: "100%" }} disabled={viewMode} placeholder="Amendment Date" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="TRN Date" name="trn_date">
                <DatePicker style={{ width: "100%" }} disabled={viewMode} placeholder="Select TRN Date" />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                label="Upload Voucher"
                name="voucher"
                rules={[
                  {
                    // required: !isEdit,
                    required: false,
                    message: "Please upload a voucher file",
                  },
                ]}
              >
                <Upload
                  fileList={fileList}
                  onChange={handleFileChange}
                  beforeUpload={() => false}
                  maxCount={1}
                  disabled={viewMode}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                >
                  <Button icon={<UploadOutlined />} disabled={viewMode} style={{ width: "100%" }}>
                    {isEdit && fileList.length > 0 ? "Change File" : "Upload"}
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="FOC Certificate Charged"
                name="is_active_foc_certificate_charged"
                valuePropName="checked"
              >
                <Switch disabled={viewMode} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item label="IOU" name="iou_id" rules={[{ required: true, message: "IOU is required" }]}>
                <Select
                  placeholder="Select IOU"
                  disabled={viewMode}
                  showSearch
                  filterOption={false}
                  onSearch={async (value) => {
                    if (value && value.length >= 2) {
                      await getIOU({ iou_number: value });
                    } else if (!value) {
                      await getIOU();
                    }
                  }}
                  onSelect={(value) => {
                    const selectedIOU = options.iou.find((iou) => iou.id === value);
                    if (selectedIOU) {
                      form.setFieldsValue({
                        iou_id: selectedIOU.id,
                      });
                    }
                  }}
                  options={options.iou.map((iou) => ({
                    label: iou.iou_number,
                    value: iou.id,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Cost Details</Divider>

          <Form.List name="cost">
            {(fields) => {
              // Filter fields based on used quantity value
              const filteredFields = fields.filter((field) => {
                const costs = form.getFieldValue("cost") || [];
                const currentCost = costs[field.name];
                if (currentCost && currentCost.type && currentCost.type.toLowerCase().includes("service charge")) {
                  return usedQuantityWatcher > 0;
                }
                return true;
              });

              return (
                <Table
                  bordered
                  dataSource={filteredFields.map((field) => ({
                    ...field,
                    key: field.key,
                    fieldName: field.name,
                  }))}
                  pagination={false}
                  columns={tableColumns}
                  summary={() => (
                    <Table.Summary fixed>
                      <Table.Summary.Row key={1}>
                        <Table.Summary.Cell index={0} colSpan={1}>
                          <Text strong>Miscellaneous Cost</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} colSpan={1}>
                          <Input
                            placeholder="Enter miscellaneous cost"
                            disabled={viewMode}
                            value={miscellaneousCost}
                            onChange={handleMiscellaneousCostChange}
                          />
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row key={2}>
                        <Table.Summary.Cell index={0} colSpan={1}>
                          <Text strong>Comment for Miscellaneous Cost</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} colSpan={1}>
                          <Input.TextArea
                            rows={4}
                            placeholder="Enter comment for miscellaneous cost"
                            disabled={viewMode}
                            value={commentMiscellaneousCost}
                            onChange={handleCommentMiscellaneousCostChange}
                          />
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row key={3}>
                        <Table.Summary.Cell index={0} colSpan={1}>
                          <Text strong>Total</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} colSpan={1}>
                          <Text strong>
                            {parseFloat(total).toFixed(2)} {DEFAULT_CURRENCY}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              );
            }}
          </Form.List>
        </Form>
      </Card>
    </div>
  );
});

BgmeaUdAmendmentForm.displayName = "BgmeaUdAmendmentForm";

export default BgmeaUdAmendmentForm;
